/*
 * BhZoneMatch — which of our delivery zones does a Google address sit in?
 *
 * Pure logic, no DOM: bh-address.js calls it with the `addressComponents` of the place the
 * customer picked, and scripts/test-zone-match.mjs runs it against real orders.
 *
 * Google names a neighbourhood in its own words ("ж.к. Люлин 10", "квартал Малинова долина",
 * "ж.к. Красно село - квартал Борово"), so both sides are normalised before comparing. Order of
 * attempts, per Google name, most specific first:
 *   1. exact      "ж.к. Младост 2"  → Младост 2
 *   2. generic    "ж.к. Дружба 2"   → Дружба (a numbered sub-district we list only as one zone;
 *                 see "Numbered sub-districts" in docs/business-rules.md)
 *   3. alias      ALIASES below, for places Google calls something we do not
 * Google's `neighborhood` is tried before `sublocality`: "м. Гърдова глава" (Бояна) carries
 * sublocality "Витоша", which is the wrong answer.
 *
 * ponytail: a hand-kept alias table, not geometry. Zone polygons would be exact but nobody has
 * drawn them; an unmatched address just falls back to the dropdown, so a gap costs one tap.
 */
const BhZoneMatch = (() => {
  // Google's name (normalised) → our zone's Bulgarian name (normalised). Each entry was checked
  // against a real order. Add one when the test or a customer shows a miss.
  const ALIASES = {
    'софия център': 'център на софия',   // #1016
    'old city center': 'център на софия', // #1055 — Google answers in English here even with language=bg
    'център': 'център на софия',
    'американски колеж': 'витоша',        // #1071, в.з. Американски колеж
    'гърдова глава': 'бояна',             // #1022, м. Гърдова глава
    'карпузица': 'княжево',               // #1068
    'студентски комплекс': 'студентски град', // Google's name for it in suggestions
  };

  const PREFIX = /^(?:ж\.?\s*к\.?|квартал|кв\.?|в\.\s*з\.|м\.|местност)\s*/;

  const norm = (s) => String(s || '')
    .toLowerCase()
    .replace(/[„“”"«»']/g, '')
    .replace(/[–—]/g, '-')
    .trim()
    .replace(PREFIX, '')
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  /**
   * @param {Array<{longText:string, types:string[]}>} components  Place.addressComponents
   * @param {Array<{handle:string, nameBG:string, nameEN?:string}>} zones  zones on offer
   * @returns {{handle:string, via:'exact'|'generic'|'alias', googleName:string} | {handle:null, googleName:string|null}}
   */
  function matchZone(components, zones) {
    const pick = (type) => ((components || []).find((c) => (c.types || []).includes(type)) || {}).longText;
    const names = [pick('neighborhood'), pick('sublocality_level_1') || pick('sublocality')].filter(Boolean);
    // Google labels the central boulevard "бул. Витоша" as квартал Витоша. Our Витоша zone is
    // the one at the foot of the mountain; postcode 1000 is the centre.
    if (names[0] && norm(names[0]) === 'витоша' && pick('postal_code') === '1000') names[0] = 'център';

    const byName = new Map();
    (zones || []).forEach((z) => [z.nameBG, z.nameEN].forEach((n) => {
      if (n && !byName.has(norm(n))) byName.set(norm(n), z.handle);
    }));

    for (const googleName of names) {
      // "Красно село - квартал Борово": the last part is the most specific.
      const parts = googleName.split(/\s+[-–—]\s+/).map(norm).reverse();
      for (const p of parts) {
        if (byName.has(p)) return { handle: byName.get(p), via: 'exact', googleName };
        // "Младост 1А" → Младост 1 if listed, else Младост.
        for (const base of [p.replace(/(\d)[а-яa-z]$/, '$1'), p.replace(/\s*\d+[а-яa-z]?$/, '')]) {
          if (base !== p && byName.has(base)) return { handle: byName.get(base), via: 'generic', googleName };
        }
        if (ALIASES[p] && byName.has(ALIASES[p])) return { handle: byName.get(ALIASES[p]), via: 'alias', googleName };
      }
    }
    return { handle: null, googleName: names[0] || null };
  }

  /**
   * The neighbourhood the customer NAMED in what they typed ("Lyulin 5 bl 520", "Младост 4 бл 401"),
   * Cyrillic or Latin (each zone's English name is its transliteration). Longest name wins, so
   * "Младост 4" beats "Младост". Google's autocomplete is weak on Sofia block numbers ("бл. 520"
   * exists in several neighbourhoods), so a named neighbourhood outranks Google's guess.
   */
  function typedZone(text, zones) {
    const t = ` ${norm(String(text || '').replace(/^\s*(ж\.?\s*к\.?|zh\.?\s*k\.?|kv\.?)\s*/i, ''))} `.replace(/[.,]/g, ' ').replace(/\s+/g, ' ');
    let best = null, len = 0;
    for (const z of zones || []) {
      for (const n of [z.nameBG, z.nameEN]) {
        const k = n && norm(n);
        if (k && k.length > len && t.includes(` ${k} `)) { best = z.handle; len = k.length; }
      }
    }
    return best;
  }

  /**
   * Does the typed address carry a house or block number? Digits inside names do not count:
   * ordinals in street names ("21-ви век", "3020-та") and numbered neighbourhoods ("Младост 4",
   * "Lyulin 5", "Зона Б-5"). "Младост 4 бл 401" and "21-ви век 12" pass; "21-ви век" does not.
   */
  function hasNumber(text, zones) {
    let t = ` ${norm(String(text || ''))} `.replace(/\d+\s*-\s*[а-яa-z]{1,3}(?![а-яa-z])/gi, ' ');
    for (const z of zones || []) {
      for (const n of [z.nameBG, z.nameEN]) {
        const k = n && norm(n);
        if (k && /\d/.test(k)) t = t.split(` ${k} `).join(' ');
      }
    }
    return /\d/.test(t);
  }

  return { matchZone, typedZone, hasNumber, norm };
})();
